import type { SyntheticEvent } from 'react'
import { createContext, useContext } from 'react'
import type { Dayjs } from 'dayjs'
import Debug from 'debug'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { StateCreator } from 'zustand/vanilla'

import type { PhotoRef } from '@jotta/grpc-connect-openapi/photo'
import { env } from '@jotta/utils/env'

import { fromTimestampNanos } from '../components/PhotoGallery/timelinev2/util'
import { hasAddPhotoMode } from '../components/PhotoGallery/useAddPhotoMode'

const debug = Debug('jotta:photos:SelectionStore')

// Selection crosses photo sources that do not expose the same payload shape.
// Timeline photos are rich enough to include capture time and content refs, while
// people photos can enter selection as sparse md5/ref data and resolve richer
// fields only when an action needs them.
export type SelectablePhoto = {
  id: string // TODO eventually this can be removed and timestampNanos can identify photo
  ref: PhotoRef | undefined // TODO eventually this will always be present
  // Sparse people-photo selections can use md5 before a full Photo is fetched.
  md5?: string
  // Only sources with capture metadata can contribute to selected-day tracking.
  timestampNanos?: bigint
  // People-photo actions may resolve this lazily after the photo is selected.
  encodedContentRef?: string
}

export interface PhotoSelectionState {
  selectedPhotos: Record<string, SelectablePhoto>
  selectedDays: Record<string, string>
  lastSelected: string | undefined
  hasSelection: boolean
  values: readonly SelectablePhoto[]
  size: number
  shiftSelectRange: readonly SelectablePhoto[]

  clear: () => void
  reset: () => void
  has: (id: string) => boolean
  hasDaySelection: (date: Dayjs) => boolean
  add: (...ids: SelectablePhoto[]) => void
  delete: (...ids: string[]) => void
  onClickToggle: (photo: SelectablePhoto, event: SyntheticEvent) => void
  onClickToggleIfActive: (
    photo: SelectablePhoto,
    event: SyntheticEvent,
  ) => boolean
  onToggleSelect: (
    photo: SelectablePhoto,
    selected: boolean,
    event: SyntheticEvent,
  ) => void
  enableShiftSelect(photos: readonly SelectablePhoto[]): void
}

const initialState = {
  lastSelected: undefined,
  selectedPhotos: {},
  selectedDays: {},
  hasSelection: false,
  values: [],
  size: 0,
  shiftSelectRange: [],
} as const

function dayFormat(date: Dayjs) {
  return date.format('L')
}

const enableSelectionStoreDevtools =
  !env.isProduction &&
  // Redux DevTools chokes on the BigInt values in photo selection state.
  (typeof window === 'undefined' || !('__REDUX_DEVTOOLS_EXTENSION__' in window))

const initializer: StateCreator<PhotoSelectionState> = (set, get) => {
  function updateSelection(
    updater: (
      state: Record<string, SelectablePhoto>,
    ) => Record<string, SelectablePhoto>,
    lastSelected?: string,
  ) {
    const previousState = get()
    const newSelection = updater(previousState.selectedPhotos)
    const values = Object.values(newSelection)
    const newSize = values.length

    if (previousState.size === 0 && newSize === 0) {
      return
    }

    const selectedDays = values.reduce((acc: Record<string, string>, photo) => {
      if (photo.timestampNanos === undefined) {
        return acc
      }

      const formattedDay = dayFormat(fromTimestampNanos(photo.timestampNanos))
      acc[formattedDay] = formattedDay
      return acc
    }, {})

    set({
      selectedPhotos: newSelection,
      hasSelection: newSize > 0,
      size: newSize,
      selectedDays,
      lastSelected,
      values,
    })
  }

  return {
    ...initialState,

    has: id => Boolean(get().selectedPhotos[id]),
    hasDaySelection: date => Boolean(get().selectedDays[dayFormat(date)]),
    add: (...photos) =>
      updateSelection(previous => {
        const newSelection = { ...previous }
        photos.forEach(photo => (newSelection[photo.id] = photo))
        return newSelection
      }, photos.at(-1)?.id),
    delete: (...ids) =>
      updateSelection(previous => {
        const newSelection = { ...previous }
        ids.forEach(id => delete newSelection[id])
        return newSelection
      }, undefined),
    clear: () => updateSelection(() => ({})),
    reset: () => set(initialState),

    enableShiftSelect: (shiftSelectRange: readonly SelectablePhoto[]) => {
      set({
        shiftSelectRange,
      })
    },

    onClickToggle: (photo, e) => {
      const currentId = photo.id
      const state = get()
      state.onToggleSelect(photo, !state.has(currentId), e)
    },

    onToggleSelect: (photo, selected, e) => {
      const currentId = photo.id
      e.preventDefault()
      e.stopPropagation()
      const el = e.currentTarget || e.target
      if (!(el instanceof HTMLElement)) {
        return
      }

      const shiftKey = Boolean((e as React.KeyboardEvent).shiftKey)

      const state = get()
      const shiftSelectRange = state.shiftSelectRange
      const lastSelected = state.lastSelected
      if (
        shiftKey &&
        shiftSelectRange.length > 0 &&
        lastSelected &&
        currentId !== lastSelected
      ) {
        /// Select/deselect range
        const [start, end] = [
          shiftSelectRange.findIndex(p => p.id === lastSelected),
          shiftSelectRange.findIndex(p => p.id === currentId),
        ].sort((a, b) => a - b)

        if (start >= 0 && end >= 0) {
          for (let i = start; i < end; i++) {
            state.add(shiftSelectRange[i])
          }
          state.add(photo)
        }
      } else if (selected) {
        state.add(photo)
      } else {
        state.delete(currentId)
      }
    },

    onClickToggleIfActive: (id, e) => {
      const shiftKey = (e as unknown as React.KeyboardEvent).shiftKey
      debug(e)
      const state = get()
      if (state.hasSelection || hasAddPhotoMode() || shiftKey) {
        state.onClickToggle(id, e)
        return true
      }
      return false
    },
  }
}

export const usePhotoSelectionStore = create<PhotoSelectionState>()(
  devtools(initializer, {
    name: 'photoSelectionStore',
    enabled: enableSelectionStoreDevtools,
  }),
)
export const usePhotoSearchSelectionStore = create<PhotoSelectionState>()(
  devtools(initializer, {
    name: 'photoSearchSelectionStore',
    enabled: enableSelectionStoreDevtools,
  }),
)

export const PhotoSelectionContext = createContext(usePhotoSelectionStore)

export const useSelectionStore = () => useContext(PhotoSelectionContext)()
